import { useEffect, useState } from "react"
import type { ConsumerBillType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { BillingInfoType } from "@/types/billing"
import { payBill } from "@/lib/api/billing"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
} from "@/components/ui/credenza"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"

import { Icons } from "../icons"

const formSchema = z.object({
  amount: z.coerce
    .number()
    .gte(1, { message: "At least ₱1" })
    .lte(100000, { message: "At most ₱100,000" }),
})

export function SubmitPayment({ row }: { row: ConsumerBillType }) {
  const [showConfirm, setShowConfirm] = useState(false)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { amount: 0 },
  })

  const {
    formState: { errors },
    clearErrors,
  } = form

  useEffect(() => {
    if (errors.amount) {
      const timer = setTimeout(() => {
        clearErrors("amount")
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [errors.amount, clearErrors])

  const mutation = useMutation({
    mutationFn: async (data: BillingInfoType) => {
      await payBill(data)
    },
    onSuccess: () => {
      toast({ description: "Payment processed successfully." })
      queryClient.invalidateQueries({ queryKey: ["consumerBill"] })
    },
    onError: () => {
      toast({
        title: "Something went wrong!",
        description: "Please try again.",
        variant: "destructive",
      })
    },
  })

  function confirmPayment() {
    const amount = form.getValues("amount")
    if (!amount || amount <= 0) {
      form.trigger("amount")
      return
    }
    setShowConfirm(true)
  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate({
      amount_paid: values.amount,
      account_num: row.acc_id,
      billing_id: row.billing_id,
    })
    setShowConfirm(false)
    form.reset()
  }

  return (
    <>
      <Form {...form}>
        <form
          noValidate
          className="flex gap-1"
          onSubmit={(e) => {
            e.preventDefault()
            confirmPayment()
          }}
        >
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="h-8 max-w-[100px]"
                    type="number"
                    placeholder="₱"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button className="h-8" type="submit">
            Pay
          </Button>
        </form>
      </Form>

      {/* ✅ Payment Confirmation Modal */}
      <Credenza open={showConfirm} onOpenChange={setShowConfirm}>
        <CredenzaContent>
          <CredenzaHeader>
            <CredenzaTitle>Confirm Payment</CredenzaTitle>
            <CredenzaDescription>
              Please confirm this payment. This action cannot be undone.
            </CredenzaDescription>
          </CredenzaHeader>

          <CredenzaBody>
            <p className="text-sm">
              You are about to pay{" "}
              <span className="font-semibold">
                ₱{form.getValues("amount").toLocaleString()}
              </span>{" "}
              for account <span className="font-semibold">{row.acc_id}</span>{" "}
              (Billing ID:{" "}
              <span className="font-semibold">{row.billing_id}</span>).
            </p>
          </CredenzaBody>

          <CredenzaFooter>
            <CredenzaClose asChild>
              <Button variant="outline">Cancel</Button>
            </CredenzaClose>

            <Button
              disabled={mutation.isPending}
              variant="default"
              onClick={form.handleSubmit(onSubmit)}
            >
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Confirm Payment
            </Button>
          </CredenzaFooter>
        </CredenzaContent>
      </Credenza>
    </>
  )
}
